import FuseScrollbars from "@fuse/core/FuseScrollbars";
import { EmailRounded, MoveToInboxRounded } from "@mui/icons-material";
import { useSelector, useDispatch } from "react-redux";
import CloseIcon from "@mui/icons-material/Close";
import InfoIcon from "@mui/icons-material/Info";
import { FormProvider, useForm } from "react-hook-form";
import {
  Avatar,
  Box,
  IconButton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import React, { useState } from "react";
import moment from "moment";
import _ from "lodash";
import { showMessage } from "app/store/fuse/messageSlice";
import CustomExportContent from "./CustomExportContent";
import { exportOrders } from "../e-commerce/store/ordersSlice";
import { downloadFromResponse } from "./AuraDownload";

const defaultToDate = moment().format("YYYY-MM-DD");
const defaultFromDate = moment().subtract(30, "days").format("YYYY-MM-DD");

export const getInitValues = (type, parentFilterType) => {
  if (type === "customExport" && parentFilterType === "customer") {
    return {
      start_date: defaultFromDate,
      end_date: defaultToDate,
      sales_person: null,
      customers: [],
    };
  }
  if (type === "customExport" && parentFilterType === "supplier") {
    return {
      start_date: defaultFromDate,
      end_date: defaultToDate,
      suppliers: [],
    };
  }
  return {
    limit: 500,
  };
};

const ExportContent = (props) => {
  const dispatch = useDispatch();
  const { selectedExportType } = props;
  const [exportAllType, setExportAllType] = useState("online");
  const [loading, setLoading] = useState(false);
  const customersListItems = useSelector(
    (state) => state.eCommerceApp.orders.customersListItems
  );
  const salesPersonListItems = useSelector(
    (state) => state.eCommerceApp.orders.salesPersonListItems
  );
  const suppliersListItems = useSelector(
    (state) => state.eCommerceApp.orders.suppliersList
  );
  const parentFilterType = props?.params?.parentFilterType ?? "customer";

  const methods = useForm({
    mode: "onChange",
    defaultValues: getInitValues(selectedExportType, parentFilterType),
  });
  const { reset, getValues } = methods;
  /**
   *Handle Online/Offline type changes
   */
  const handleExportTypeChange = (event, newExportType) => {
    if (newExportType !== null) {
      setExportAllType(newExportType);
    }
  };
  /**
   * Dialog Title
   */
  function getTitle() {
    if (selectedExportType === "exportAll") return "Export All";
    return "Custom Export";
  }
  /**
   * To check Export All is currently selected
   */
  function isAllExportSelected() {
    return selectedExportType === "exportAll";
  }
  /**
   * To check Online toggle from Export All is currently selected
   */
  function isOnlineExportSelected() {
    return selectedExportType === "exportAll" && exportAllType === "online";
  }
  /**
   * To check offline toggle from Export All is currently selected
   */
  function isOfflineExportSelected() {
    return selectedExportType === "exportAll" && exportAllType === "offline";
  }
  /**
   * To check Custom Export is currently selected
   */
  function isCustomExportSelected() {
    return selectedExportType === "customExport";
  }
  /**
   * Handle export submit
   */
  const handleExportSubmit = () => {
    setLoading(true);
    const exportApiType = isOfflineExportSelected() ? "Offline" : "Online";

    const formValues = getValues();
    const inputData = _.cloneDeep(formValues);
    inputData.filter_type = parentFilterType; // for customer_orders / supplier_orders

    if (inputData?.customers?.length > 0)
      inputData.customers = inputData.customers.map((customer) => customer.id);
    if (inputData?.suppliers?.length > 0)
      inputData.suppliers = inputData.suppliers.map((supplier) => supplier.id);

    const apiData = {
      payload: inputData,
      params: { stage: props?.params?.stage, type: exportApiType },
    };

    dispatch(exportOrders(apiData)).then((response) => {
      if (!response?.error) {
        if (exportApiType === "Online") {
          downloadFromResponse(response?.payload);
        } else {
          dispatch(
            showMessage({
              message: "Reports will be shared in the email",
              variant: "info",
              anchorOrigin: {
                vertical: "top",
                horizontal: "left",
              },
            })
          );
        }
      }
      setLoading(false);
    });
  };
  /**
   * Handle close dialog action
   */
  const handleClose = () => {
    reset();
    props.handleClose();
  };

  return (
    <FormProvider {...methods}>
      <FuseScrollbars>
        <Box
          component="header"
          className="px-12"
          sx={{
            width: "100%",
            height: 60,
            bgcolor: "#efefef",
            borderBottom: 1,
            borderColor: "#a1b0c6",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography className="text-18 font-bold">{getTitle()}</Typography>
          <IconButton
            size="small"
            sx={{
              bgcolor: "white",
              border: 1,
              borderColor: "#87959f",
              borderRadius: 4,
              width: 32,
              height: 32,
            }}
            onClick={() => handleClose()}
          >
            <CloseIcon className="!text-18" size="small" color="primary" />
          </IconButton>
        </Box>

        <Box className="p-16">
          {isAllExportSelected() && (
            <>
              {/* Toggle Buttons */}
              <ToggleButtonGroup
                value={exportAllType}
                exclusive
                onChange={handleExportTypeChange}
                sx={{ height: 40, width: "100%" }}
                disabled={loading}
              >
                <ToggleButton
                  value="online"
                  className="text-16 capitalize w-1/2 border ronunded-md"
                  sx={(theme) => ({
                    bgcolor: "#fff",
                    color: "#696f8c",
                    "&.Mui-selected": {
                      bgcolor: theme.palette.secondary.main,
                      color: theme.palette.common.white,
                      "&:hover": {
                        bgcolor: theme.palette.secondary.dark,
                      },
                    },
                  })}
                >
                  Online
                </ToggleButton>
                <ToggleButton
                  value="offline"
                  className="text-16 capitalize w-1/2  border ronunded-md"
                  sx={(theme) => ({
                    bgcolor: "#fff",
                    "&.Mui-selected": {
                      bgcolor: theme.palette.secondary.main,
                      color: theme.palette.common.white,
                      "&:hover": {
                        bgcolor: theme.palette.secondary.dark,
                      },
                    },
                  })}
                >
                  Offline
                </ToggleButton>
              </ToggleButtonGroup>

              {/* Center Icon Section */}
              <Box sx={{ display: "flex", justifyContent: "center", mt: 10 }}>
                <Avatar
                  sx={{
                    width: 100,
                    height: 100,
                    bgcolor: "#36a4fe",
                  }}
                >
                  {isOfflineExportSelected() && (
                    <EmailRounded className="text-48" />
                  )}
                  {isOnlineExportSelected() && (
                    <MoveToInboxRounded className="text-48" />
                  )}
                </Avatar>
              </Box>

              {/* Information section */}
              <Box
                className="mt-24"
                sx={{ display: "flex", justifyContent: "center" }}
              >
                <Box
                  className="flex flex-row text-14 rounded-md text-[#0d84e5] p-12 text-wrap text-center"
                  sx={{
                    bgcolor: "#e8f1ff",
                    color: "#0d84e5",
                    "& .MuiAlert-icon": {
                      color: "#0d84e5",
                    },
                  }}
                >
                  <InfoIcon fontSize="small" className="mr-8" />
                  <Typography className="text-13 font-bold">
                    {isOnlineExportSelected()
                      ? "Maximum of 500 records can be exported"
                      : "Reports will be shared in the email"}
                  </Typography>
                </Box>
              </Box>
            </>
          )}

          {isCustomExportSelected() && (
            <>
              <CustomExportContent
                {...props}
                customersListItems={customersListItems}
                salesPersonListItems={salesPersonListItems}
                suppliersListItems={suppliersListItems}
              />
            </>
          )}
        </Box>

        {/* Footer */}
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            width: "100%",
            height: 60,
            bgcolor: "#f1f5f9",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Stack direction="row" spacing={2}>
            <LoadingButton
              variant="contained"
              color="secondary"
              className="text-14 px-12 rounded-md font-medium"
              sx={{
                minWidth: "110px",
                height: "35px",
                minHeight: "35px",
                maxHeight: "35px",
              }}
              loading={loading}
              loadingIndicator={
                isOfflineExportSelected() ? "Submitting..." : "Downloading..."
              }
              onClick={() => handleExportSubmit()}
            >
              {isOfflineExportSelected() ? "Submit" : "Download"}
            </LoadingButton>
          </Stack>
        </Box>
      </FuseScrollbars>
    </FormProvider>
  );
};

export default ExportContent;
